// useDemandBatchStatus — server-engine adapter for a single batch as seen by
// the apartment manager who owns it (Sprint 6).
//
// Wraps `get_demand_batch_status(batch_id)`. PRIVACY: platform-only, returns
// `day_flags: []`. The client fallback is computed from the batch's own
// platform bookings supplied by the caller.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildClientFallbackSummary,
  fetchDemandBatchStatus,
  trackSchedulerEngineFallback,
  type ClientFallbackInput,
  type SchedulerError,
  type SchedulerSource,
  type ServerSchedulerSummary,
} from "@/lib/scheduler";
import type { UseSchedulerState } from "./types";

export interface UseDemandBatchStatusOptions {
  batchId: string | null | undefined;
  clientFallback?: () => ClientFallbackInput;
  enabled?: boolean;
}

export function useDemandBatchStatus(
  options: UseDemandBatchStatusOptions,
): UseSchedulerState<ServerSchedulerSummary> {
  const { batchId, clientFallback, enabled = true } = options;
  const [data, setData] = useState<ServerSchedulerSummary | null>(null);
  const [source, setSource] = useState<SchedulerSource | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SchedulerError | null>(null);
  const [tick, setTick] = useState(0);
  const fallbackRef = useRef(clientFallback);
  fallbackRef.current = clientFallback;

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !batchId) {
      setData(null);
      setSource(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      const result = await fetchDemandBatchStatus({ batchId });
      if (cancelled) return;
      if (result.data) {
        setData(result.data);
        setSource(result.source);
        setError(null);
        setIsLoading(false);
        return;
      }
      const recoverable = !result.error || result.error.recoverable;
      if (recoverable && fallbackRef.current) {
        const fallback = buildClientFallbackSummary(fallbackRef.current());
        setData(fallback);
        setSource("client_fallback");
        setError(null);
        trackSchedulerEngineFallback({
          surface: "demand_control_tower",
          scope: "demand_batch",
          reason: result.error?.message ?? "no_data",
          errorCode: result.error?.code,
          recoverable: true,
        });
      } else {
        setData(null);
        setSource(null);
        setError(result.error);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [batchId, enabled, tick]);

  return useMemo(
    () => ({ data, source, isLoading, error, refetch }),
    [data, source, isLoading, error, refetch],
  );
}
